import { ReactElement, useCallback, useMemo } from 'react'
import { DEFAULT_STOREY_UPLOAD_IMAGE_CONFIG } from '@constants'
import { merge, reject, reverse, sortBy, toNumber } from 'lodash-es'
import { Stack, Typography } from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { useCameraStore } from '@modugen/ui-toolkit'
import { roofStoreyKey, useModelStore } from '@editorStores'
import { useStoreyImages } from '@editorHooks'
import StoreyImageUpload from '../StoreyImageUpload'

/**
 * Drawer panel to upload a background plan image per storey. Reuses the per-storey
 * `StoreyImageUpload` control and the shared `useStoreyImages` request hook, so it can be embedded in
 * any drawer (e.g. the guided IFC import "Pläne hochladen" step) as well as the storey controls.
 */
const StoreyImageUploadDrawer = (): ReactElement => {
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const {
    storeyImages,
    setStoreyImage,
    removeStoreyImage,
    updateStoreyImageTransform,
    toggleStoreyImageVisibilityIn2D,
    toggleStoreyImageVisibilityIn3D,
    isUploadingStoreyImage,
    isRemovingStoreyImage,
  } = useStoreyImages()

  // The roof ("Dach") is excluded: a roof plan cannot be displayed, so it must not be uploadable.
  const storeys = useMemo(
    () =>
      reverse(
        sortBy(
          reject(Array.from(availableStoreys), storey => storey === roofStoreyKey),
          storey => toNumber(storey),
        ),
      ),
    [availableStoreys],
  )

  // Merge any existing transform with the defaults so a freshly uploaded image gets sane placement.
  const handleImageUpload = useCallback(
    (file: File, storeyKey: string, options?: { pdfPage?: number }) => {
      const data = merge({}, DEFAULT_STOREY_UPLOAD_IMAGE_CONFIG, storeyImages[storeyKey])
      setStoreyImage(storeyKey, file, data, options?.pdfPage)
    },
    [setStoreyImage, storeyImages],
  )

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Laden Sie je Stockwerk einen Hintergrundplan hoch.
      </Typography>

      {storeys.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Keine Stockwerke vorhanden. Bitte zuerst die Gebäudedimensionen festlegen.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {storeys.map(storey => (
            <Stack
              key={storey}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="body2">{storeyToLabel(storey)}</Typography>
              <StoreyImageUpload
                storey={storey}
                onUpload={handleImageUpload}
                onRemove={removeStoreyImage}
                onUpdateTransform={updateStoreyImageTransform}
                onToggleVisibility2D={toggleStoreyImageVisibilityIn2D}
                onToggleVisibility3D={toggleStoreyImageVisibilityIn3D}
                hasImage={!!storeyImages[storey]}
                isVisible2D={storeyImages[storey]?.isVisible2D ?? true}
                isVisible3D={storeyImages[storey]?.isVisible3D ?? false}
                isOrthographic={isOrthographic}
                transform={storeyImages[storey] || DEFAULT_STOREY_UPLOAD_IMAGE_CONFIG}
                isUploading={isUploadingStoreyImage}
                isRemoving={isRemovingStoreyImage}
              />
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  )
}

export default StoreyImageUploadDrawer
